/**
 * App.jsx - THE MAP OF THE WHOLE WEBSITE
 * --------------------------------------
 * Every address (URL) on the site is listed here, paired with the page that
 * should be shown for it. For example "/marketplace" shows <Marketplace />.
 *
 * <ProtectedRoute> around a page means you must be logged in to see it, and
 * `role="student"` means only students can.
 */

import { Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

import { useAuth } from './hooks/useAuth';
import Navbar from './components/Navbar';
import NotificationPrompt from './components/NotificationPrompt';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';
import OffersOnly from './components/OffersOnly';

import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ConfirmEmail from './pages/ConfirmEmail';
import GuardianConsent from './pages/GuardianConsent';
import WithdrawConsent from './pages/WithdrawConsent';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ReportIssue from './pages/ReportIssue';
import EmailOfflineBanner from './components/EmailOfflineBanner';
import Terms from './pages/Terms';
import SignupChoice from './pages/SignupChoice';
import UserSignup from './pages/UserSignup';
import StudentSignup from './pages/StudentSignup';
import Marketplace from './pages/Marketplace';
import ChoreDetail from './pages/ChoreDetail';
import CreateChore from './pages/CreateChore';
import Dashboard from './pages/Dashboard';
import ManageChore from './pages/ManageChore';
import StudentDirectory from './pages/StudentDirectory';
import UserDirectory from './pages/UserDirectory';
import StudentProfile from './pages/StudentProfile';
import UserProfile from './pages/UserProfile';
import Notifications from './pages/Notifications';
import Messages from './pages/Messages';
import MessageThread from './pages/MessageThread';
import Moderation from './pages/Moderation';
import Subscribe from './pages/Subscribe';
import Support from './pages/Support';
import Offers from './pages/Offers';
import OfferDetail from './pages/OfferDetail';
import MyOffer from './pages/MyOffer';
import AvailableStudents from './pages/AvailableStudents';
import SettingsPage from './pages/SettingsPage';
import NotFound from './pages/NotFound';

/** Jumps back to the top of the page whenever you navigate somewhere new. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * ONE CONVERSATION IS A SCREEN, NOT A DOCUMENT.
 *
 * Every other page here is a document: it starts at the top, you read down it,
 * and the footer is waiting at the bottom. A message thread is the opposite
 * shape - the newest line is at the BOTTOM and the box you type into has to be
 * under your thumb without hunting for it.
 *
 * Treating it as a document meant a phone showed a header, a notice, a stub of
 * conversation, and then the compose box below the fold with a page of footer
 * links under that. So this one route fills the window instead: the list of
 * messages is the only thing that scrolls, and there is no footer beneath it.
 * That is the layout every messaging app uses, and people already know it.
 */
function isConversation(pathname) {
  // Two shapes: /messages/<choreId> and /messages/offer/<offerId>.
  return /^\/messages\/(offer\/)?[^/]+$/.test(pathname);
}

export default function App() {
  // Only needed for the one-time notification prompt below.
  const { user } = useAuth();
  const { pathname } = useLocation();
  const conversation = isConversation(pathname);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      {/* Under the bar and above everything else, on every page including the
          signed-out ones - the people who most need to know that email is off
          are the ones who cannot log in. */}
      <EmailOfflineBanner />

      <main className={conversation ? 'page page-screen' : 'page'}>
        <Routes>
          {/* ---------- Anyone can see these ---------- */}
          <Route path="/" element={<HomePage />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/chores/:id" element={<ChoreDetail />} />

          {/* PROFILES REQUIRE A LOGIN.
              These pages show children's names, ages, school years and
              photographs. The API refuses them to anybody signed out, and
              these guards stop the page even trying - so a logged-out visitor
              gets the login screen rather than a broken, empty profile. */}
          <Route
            path="/students"
            element={
              <ProtectedRoute>
                <StudentDirectory />
              </ProtectedRoute>
            }
          />
          {/* Moderators only, and the SERVER is what enforces that -
              requireModerator on GET /api/users. This guard only keeps a
              signed-out visitor off the page; anybody else who reaches it
              gets an empty list and a 404 from the API. */}
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UserDirectory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/students/:id"
            element={
              <ProtectedRoute>
                <StudentProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id"
            element={
              <ProtectedRoute>
                <UserProfile />
              </ProtectedRoute>
            }
          />

          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupChoice />} />
          <Route path="/signup/user" element={<UserSignup />} />
          <Route path="/signup/student" element={<StudentSignup />} />

          {/* Password reset - both reached from an email, no login needed */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Confirming an email address, also from a link and also without a
              login: the person is often reading their mail on a phone they
              have never signed in on. */}
          <Route path="/confirm-email/:token" element={<ConfirmEmail />} />

          {/* The two pages a PARENT lands on from email, neither needing a
              login. `withdraw` is listed FIRST: React Router picks the more
              specific path, but keeping them in this order means nobody has
              to remember that it does. */}
          <Route path="/consent/withdraw/:token" element={<WithdrawConsent />} />
          <Route path="/consent/:token" element={<GuardianConsent />} />

          {/* Legal */}
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />

          {/* ---------- Must be logged in ---------- */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          {/* Reporting that the SITE is broken. Reporting a PERSON is the
              report button on their profile and goes somewhere else entirely -
              the screen itself says so. */}
          <Route
            path="/report-issue"
            element={
              <ProtectedRoute>
                <ReportIssue />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            }
          />
          {/* Messages about an agreed job. The server decides who may see a
              thread and answers 404 to anybody else, so these need no role -
              being logged in is enough to ask the question. */}
          <Route
            path="/messages"
            element={
              <ProtectedRoute>
                <Messages />
              </ProtectedRoute>
            }
          />
          {/* Above /messages/:choreId, so the word "offer" is not read as
              the id of a job. */}
          <Route
            path="/messages/offer/:offerId"
            element={
              <ProtectedRoute>
                <MessageThread />
              </ProtectedRoute>
            }
          />
          <Route
            path="/messages/:choreId"
            element={
              <ProtectedRoute>
                <MessageThread />
              </ProtectedRoute>
            }
          />

          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Moderators only - the API returns 404 to everybody else, so the
              page simply shows "not found" if you are not one. */}
          <Route
            path="/moderation"
            element={
              <ProtectedRoute>
                <Moderation />
              </ProtectedRoute>
            }
          />

          {/* ---------- Students only ---------- */}
          {/* Where a student says what they can do and when they are free,
              and answers the people who have asked to book them.

              Archived with the rest of offers on 17 October 2026 - see
              components/OffersOnly.jsx. While offers are off, an old link
              here lands on the student's dashboard instead. */}
          <Route
            path="/my-offer"
            element={
              <ProtectedRoute role="student">
                <OffersOnly otherwise="/dashboard">
                  <MyOffer />
                </OffersOnly>
              </ProtectedRoute>
            }
          />

          {/* ---------- Chore posters only ---------- */}
          <Route
            path="/chores/new"
            element={
              <ProtectedRoute role="user">
                <CreateChore />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chores/:id/edit"
            element={
              <ProtectedRoute role="user">
                <CreateChore />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/chores/:id"
            element={
              <ProtectedRoute role="user">
                <ManageChore />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscribe"
            element={
              <ProtectedRoute role="user">
                <Subscribe />
              </ProtectedRoute>
            }
          />
          {/* "Available students" - how many students are ready to work, and
              at what. Since 17 October 2026 this is what that tab shows; the
              list of offers below it was archived. Posters only, like the
              list it replaced: the server refuses anybody else. */}
          <Route
            path="/available-students"
            element={
              <ProtectedRoute role="user">
                <AvailableStudents />
              </ProtectedRoute>
            }
          />

          {/* Students who have said when they are free.
              Posters only, and deliberately so: this is a list of children and
              their movements, which is why - unlike the chore marketplace - it
              is not public and not visible to other students either.

              ARCHIVED, 17 October 2026: both pages still exist, and come back
              with OFFERS_ENABLED=true on the server. Until then an old link to
              either one lands on the counts above. */}
          <Route
            path="/offers"
            element={
              <ProtectedRoute role="user">
                <OffersOnly otherwise="/available-students">
                  <Offers />
                </OffersOnly>
              </ProtectedRoute>
            }
          />
          <Route
            path="/offers/:id"
            element={
              <ProtectedRoute role="user">
                <OffersOnly otherwise="/available-students">
                  <OfferDetail />
                </OffersOnly>
              </ProtectedRoute>
            }
          />

          {/* Donations. Posters only - a student must never be asked for
              money, and role="user" is what sends them away if they try. */}
          <Route
            path="/support"
            element={
              <ProtectedRoute role="user">
                <Support />
              </ProtectedRoute>
            }
          />

          {/* ---------- Anything else ---------- */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>

      {/* The one-time ask, just after a student signs up. Renders nothing
          unless it is actually due. */}
      <NotificationPrompt user={user} />

      {!conversation && <Footer />}
    </>
  );
}
